var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MeasurementSystem } from '@seddi/core';
import log from 'loglevel';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setStyleSceneFromStyleAggregatorData } from '../actions/setScene';
import { fetchStyleAggregatorData, StyleNotFoundError } from '../api/styleAggregator';
import { clearMockupLogos, setMockupLogos, setSceneIsLoading, setStyle, setStyleNotFoundMessage, setStylePreviewUrl, } from '../store/appSlice';
import { waitForSceneReady } from '../appContext';
const DEFAULT_CONTROLS_LAYOUT = 'tl';
const isValidHexColor = (value) => typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
const DEFAULT_UNITS = MeasurementSystem.Imperial;
const parseUnits = (value) => {
    const v = value === null || value === void 0 ? void 0 : value.toLowerCase();
    if (v === 'imperial')
        return MeasurementSystem.Imperial;
    if (v === 'metric')
        return MeasurementSystem.Metric;
    return DEFAULT_UNITS;
};
export const useUnitsFromUrl = () => {
    var _a;
    const [searchParams] = useSearchParams();
    return (_a = parseUnits(searchParams.get('units'))) !== null && _a !== void 0 ? _a : DEFAULT_UNITS;
};
export const useShowGraphicsPanelFromUrl = () => {
    const [searchParams] = useSearchParams();
    return searchParams.get('logos') === 'true';
};
const parseIdleAnimationMode = (raw) => {
    const v = raw === null || raw === void 0 ? void 0 : raw.toLowerCase();
    if (v === 'rotate')
        return 'rotate';
    return 'static';
};
export const useIdleAnimationModeFromUrl = () => {
    const [searchParams] = useSearchParams();
    return parseIdleAnimationMode(searchParams.get('idle'));
};
const parseControlsLayout = (value) => {
    if (value === 'tl' || value === 'tr' || value === 'bl' || value === 'br') {
        return value;
    }
    return DEFAULT_CONTROLS_LAYOUT;
};
const parseControlsStyle = (raw) => {
    var _a, _b, _c;
    if (!raw) {
        return undefined;
    }
    try {
        const decoded = decodeURIComponent(raw);
        const parsed = JSON.parse(decoded);
        const textColorSource = ((_a = parsed.t) !== null && _a !== void 0 ? _a : parsed.textColor);
        const backgroundColorSource = ((_b = parsed.bg) !== null && _b !== void 0 ? _b : parsed.backgroundColor);
        const borderColorSource = ((_c = parsed.bd) !== null && _c !== void 0 ? _c : parsed.borderColor);
        const textColor = isValidHexColor(textColorSource) ? textColorSource : undefined;
        const backgroundColor = isValidHexColor(backgroundColorSource)
            ? backgroundColorSource
            : undefined;
        const borderColor = isValidHexColor(borderColorSource) ? borderColorSource : undefined;
        if (!textColor && !backgroundColor && !borderColor) {
            return undefined;
        }
        return {
            textColor,
            backgroundColor,
            borderColor,
        };
    }
    catch (error) {
        log.warn('Failed to parse "controls_style" query parameter.', error);
        return undefined;
    }
};
export const useStyleFromUrl = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [brandLogoPolicy, setBrandLogoPolicy] = useState({
        isMockupMode: false,
        useBrandLogos: false,
    });
    const getEffectiveUseBrandLogos = (isMockupMode, data) => {
        var _a;
        if (isMockupMode) {
            return Boolean((_a = data.mockup) === null || _a === void 0 ? void 0 : _a.useBrandLogos);
        }
        return Boolean(data.isBrandApproved);
    };
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const controller = new AbortController();
        const apiKey = (_a = searchParams.get('api_key')) !== null && _a !== void 0 ? _a : '';
        const brand = (_b = searchParams.get('brand')) !== null && _b !== void 0 ? _b : '';
        const styleNumber = (_c = searchParams.get('style_number')) !== null && _c !== void 0 ? _c : '';
        const colorway = (_d = searchParams.get('colorway')) !== null && _d !== void 0 ? _d : undefined;
        const mockupId = (_e = searchParams.get('mockup_id')) !== null && _e !== void 0 ? _e : undefined;
        const isMockupMode = Boolean(mockupId);
        setBrandLogoPolicy({
            isMockupMode,
            useBrandLogos: false,
        });
        if (!apiKey) {
            log.warn('No api key provided or missing required query params. Skipping style load.');
            dispatch(setStyleNotFoundMessage('Missing required "api_key" query parameter.'));
            return () => {
                controller.abort();
            };
        }
        if (!isMockupMode) {
            if (!brand || !styleNumber || !colorway) {
                log.error('Missing required query params "brand", "style_number" and/or "colorway".');
                dispatch(setStyleNotFoundMessage('Missing required query parameters "brand", "style_number" and/or "colorway".'));
                return () => {
                    controller.abort();
                };
            }
        }
        const load = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            try {
                dispatch(setSceneIsLoading(true));
                dispatch(setStylePreviewUrl(null));
                dispatch(setStyleNotFoundMessage(null));
                const data = yield fetchStyleAggregatorData(isMockupMode
                    ? {
                        apiKey,
                        mockupId: mockupId,
                        signal: controller.signal,
                    }
                    : {
                        apiKey,
                        brand,
                        styleNumber,
                        colorway,
                        signal: controller.signal,
                    });
                const previewUrl = (_b = (_a = data.mockup) === null || _a === void 0 ? void 0 : _a.preview) !== null && _b !== void 0 ? _b : data.style.preview;
                dispatch(setStylePreviewUrl(previewUrl !== null && previewUrl !== void 0 ? previewUrl : null));
                setBrandLogoPolicy({
                    isMockupMode,
                    useBrandLogos: getEffectiveUseBrandLogos(isMockupMode, data),
                });
                dispatch(setStyle(data.style));
                const editorSceneLogos = (_d = (_c = data.mockup) === null || _c === void 0 ? void 0 : _c.liveblocksData) === null || _d === void 0 ? void 0 : _d.editorSceneLogos;
                const hideTearAwayLabel = Boolean(editorSceneLogos === null || editorSceneLogos === void 0 ? void 0 : editorSceneLogos.hideTearAwayLabel);
                if ((editorSceneLogos === null || editorSceneLogos === void 0 ? void 0 : editorSceneLogos.logos) && editorSceneLogos.logos.length > 0) {
                    dispatch(setMockupLogos({
                        logos: editorSceneLogos.logos,
                        hideTearAwayLabel,
                    }));
                }
                else {
                    dispatch(clearMockupLogos());
                }
                const effectiveUseBrandLogos = getEffectiveUseBrandLogos(isMockupMode, data);
                yield setStyleSceneFromStyleAggregatorData(dispatch, data, hideTearAwayLabel, effectiveUseBrandLogos);
                yield Promise.race([
                    waitForSceneReady(),
                    new Promise(resolve => setTimeout(resolve, 15000)),
                ]);
            }
            catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }
                if (error instanceof StyleNotFoundError) {
                    log.error(error.message);
                    dispatch(setStyleNotFoundMessage(error.message));
                    dispatch(setStyle(null));
                    return;
                }
                log.error('Error loading style from URL:', error);
            }
            finally {
                dispatch(setSceneIsLoading(false));
            }
        });
        void load();
        return () => {
            controller.abort();
        };
    }, [dispatch, searchParams]);
    return brandLogoPolicy;
};
export const useViewerControlsFromUrl = () => {
    const [searchParams] = useSearchParams();
    const controlsLayoutRaw = searchParams.get('controls_layout');
    const controlsStyleRaw = searchParams.get('controls_style');
    const controlsLayout = parseControlsLayout(controlsLayoutRaw);
    const controlsStyle = parseControlsStyle(controlsStyleRaw);
    return {
        controlsLayout,
        controlsStyle,
    };
};
